/**
 * Wer darf eine Geste zum Wecken von WebKit-Ton nutzen? (17.10.2026, chat-elemente-plan Schritte A und B)
 *
 * ## Der Anlass
 *
 * Spielt in der App die native Stimme (PawbotStimme, `PlayAndRecord`, Wach-Wort lauscht), weckte bisher JEDE Berührung
 * im Web zwei Wege: `entsperrePcm` (`AudioContext.resume()`) und `entsperreDateiSpieler` (`<audio>.play(STILLE_WAV)`).
 * WebKit aktiviert dabei seine Tonsitzung, iOS unterbricht die Engine der App — Journal 17.10.: 17 von 35 ruckelnden
 * Wischen mit `unterbrochen` am Beginn. In der App braucht Ton keine Geste; dort darf eine Berührung nichts wecken,
 * solange die native Stimme spielt oder der Motor die Tonsitzung hält (der Kontext schläft absichtlich).
 *
 * ## Was hier steht
 *
 * - Zähler „native Stimme spielt“ (`nativBeginnt`/`nativEndet`, gesetzt von stimme-nativ.ts) — eigenes kleines Modul,
 *   damit tts-pcm.ts und datei-spieler.ts nicht die Brücke (native-voice) laden müssen.
 * - `gesteDarfWecken(weg)`: antwortet und schreibt die Journalzeile `[geste-weckt] weg=… nativ=… app=… motor=… seit=…ms
 *   blockiert=…` (nur Wörter und Zahlen, gleiche Zeile höchstens alle 2 s). Browser/Mac ohne native Stimme: immer ja.
 *
 * Entsperren geht nicht verloren: `entsperrt` im Datei-Spieler bleibt falsch, der Kontext bleibt `suspended` — die nächste
 * Geste nach dem Ende der nativen Stimme holt es nach, ein Web-Stück weckt seinen Kontext ohnehin selbst.
 */
import { meldeSpur } from "./mikro-spur.js";

export type GesteWeg = "pcm-resume" | "datei-stille";

let nativLaufend = 0;
let nativSeit = 0;
let motorHaelt: () => boolean = () => false;
const zuletzt = new Map<string, number>();
const vorherGesperrt = new Map<string, boolean>();
const DROSSEL_MS = 2_000;

/** Eine native Stimme beginnt (Stück von der App angenommen). */
export function nativBeginnt(jetzt = Date.now()): void {
  if (nativLaufend === 0) nativSeit = jetzt;
  nativLaufend++;
}

/** Eine native Stimme ist zu Ende (Ende, Fehler oder Abbruch). */
export function nativEndet(): void {
  nativLaufend = Math.max(0, nativLaufend - 1);
}

/** Spielt gerade eine native Stimme? */
export function nativSpielt(): boolean {
  return nativLaufend > 0;
}

/** tts-pcm.ts meldet, ob der Motor die App-Tonsitzung hält (Kontext schläft absichtlich). */
export function setzeMotorPruefer(f: () => boolean): void {
  motorHaelt = f;
}

function inDerApp(): boolean {
  const c = (globalThis as { Capacitor?: { isNativePlatform?: () => boolean } }).Capacitor;
  return c?.isNativePlatform?.() === true;
}

/**
 * Eine Geste würde jetzt WebKit-Ton wecken: darf sie? `false` nur in der App, solange die native Stimme spielt oder der
 * Motor die Tonsitzung hält. Schreibt die Journalzeile. Wirft nie.
 */
export function gesteDarfWecken(weg: GesteWeg, jetzt = Date.now()): boolean {
  let app = false;
  let nativ = false;
  let motor = false;
  try {
    app = inDerApp();
    nativ = nativSpielt();
    motor = app && motorHaelt();
  } catch {
    /* ein Fühler darf nie stören */
  }
  const blockiert = app && (nativ || motor);
  try {
    // Nur in der App, nur bei Sperre (höchstens 1 Zeile je Weg und 2 s) oder beim Wechsel gesperrt -> frei.
    const warGesperrt = vorherGesperrt.get(weg) === true;
    vorherGesperrt.set(weg, blockiert);
    if (app) {
      const vorher = zuletzt.get(weg);
      const gedrosselt = vorher !== undefined && jetzt - vorher < DROSSEL_MS;
      if ((blockiert && !gedrosselt) || (!blockiert && warGesperrt)) {
        zuletzt.set(weg, jetzt);
        const seit = nativ ? Math.max(0, jetzt - nativSeit) : 0;
        meldeSpur(
          "geste-weckt",
          `weg=${weg} nativ=${nativ ? "ja" : "nein"} app=ja motor=${motor ? "ja" : "nein"} seit=${seit}ms blockiert=${blockiert ? "ja" : "nein"}`,
        );
      }
    }
  } catch {
    /* Journal ist best-effort */
  }
  return !blockiert;
}

/** Nur Tests. */
export function _gesteWacheZuruecksetzen(): void {
  nativLaufend = 0;
  nativSeit = 0;
  zuletzt.clear();
  vorherGesperrt.clear();
}
